import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { clamp, isNumber } from 'lodash';
import { Box, Typography, Slider } from '@mui/material';
import { getUserSettings } from '@surfline/web-common';

import { MDY } from 'common/constants';
import { useAppSelector } from 'stores/hooks';
import type {
  GraphId,
  GraphScrubbedEvent,
} from 'containers/ForecastDataVisualizer/GraphTooltips/GraphTooltipProvider/GraphTooltipProvider';
import { formatTimestampInTimezone } from 'utils/time';

import styles from './ForecastGraphSlider.module.scss';

const NOW_LABEL_WIDTH = 25;
const POSITION_TOLERANCE_THRESHOLD = 1;
const SLIDER_VALUE_UPDATE_THRESHOLD = 0.1;

export interface ForecastGraphTimelineSliderProps {
  customWidth?: number;
  /** Root element `data-testid` (e.g. `forecast-graph-slider` for swell spectra). */
  dataTestId?: string;
  graphId: GraphId;
  graphWidth: number;
  leftLastDataPointPosition?: number;
  leftNowPosition?: number | null;
  leftPosition?: number | null;
  numberOfDays: number;
  renderTooltip: (xPercentage: number, graphId: GraphId) => void;
  timeLabel?: string | null;
  timestamp?: number | null;
  trackGraphScrubbed: GraphScrubbedEvent;
  utcOffset: number;
}

const SliderTimeLabel = ({
  utcOffset,
  sliderHandleWidth,
  containerWidth,
  leftPosition,
  timeLabel,
  timestamp,
}: {
  utcOffset: number;
  sliderHandleWidth: number;
  containerWidth: number;
  leftPosition?: number | null;
  timeLabel?: string | null;
  timestamp?: number | null;
}) => {
  const timeLabelRef = useRef<HTMLDivElement | null>(null);
  const timeLabelWidth = timeLabelRef?.current?.offsetWidth || 0;

  const settings = useAppSelector(getUserSettings);
  const userDateFormat = settings?.date?.format;
  const dateFormat = userDateFormat === MDY ? 'EEEE M/d hh:mmaaa' : 'EEEE d/M hh:mmaaa';

  const timeLabelText = useMemo(() => {
    const formattedLabel = formatTimestampInTimezone(timestamp || 0, utcOffset || 0, dateFormat);
    return timeLabel === 'Now' ? 'Now' : formattedLabel;
  }, [dateFormat, timeLabel, timestamp, utcOffset]);

  const getTimeLabelLeftPosition = useCallback(() => {
    if (!isNumber(leftPosition)) return { left: -1 };

    const timeLabelPosition = leftPosition - timeLabelWidth / 2;

    if (timeLabelWidth / 2 >= leftPosition) {
      return { left: 0 };
    }

    if (leftPosition + timeLabelWidth / 2 >= containerWidth) {
      return {
        left: containerWidth - sliderHandleWidth - timeLabelWidth,
      };
    }

    return { left: timeLabelPosition };
  }, [containerWidth, leftPosition, sliderHandleWidth, timeLabelWidth]);

  return (
    <div className={styles.timeLabelContainer}>
      <Typography
        className={styles.timeLabel}
        data-testid="forecast-slider-time-label"
        ref={timeLabelRef}
        style={getTimeLabelLeftPosition()}
        variant="callout1"
      >
        {timeLabelText}
      </Typography>
    </div>
  );
};

const SliderDayDividers = ({
  containerWidth,
  dividerOffsetPx,
  numberOfDays,
}: {
  containerWidth: number;
  dividerOffsetPx: number;
  numberOfDays: number;
}) => {
  const sliderRailDividersStyle = useCallback(
    (index: number) => ({
      left: `${(containerWidth / numberOfDays) * (index + 1) + dividerOffsetPx}px`,
    }),
    [containerWidth, dividerOffsetPx, numberOfDays],
  );

  const dividers = useMemo(
    () => (
      <>
        {Array(numberOfDays)
          .fill(null)
          .map((day, index) => {
            const isLastDayMidnightTick = index + 1 === numberOfDays;
            return !isLastDayMidnightTick ? (
              <div
                key={`${index + 1}`}
                className={styles.sliderRailDividers}
                data-testid="forecast-graph-slider-day-dividers"
                style={sliderRailDividersStyle(index)}
              />
            ) : null;
          })}
      </>
    ),
    [numberOfDays, sliderRailDividersStyle],
  );

  return dividers;
};

const DayDividers = React.memo(SliderDayDividers);

const ForecastGraphTimelineSlider: React.FC<ForecastGraphTimelineSliderProps> = ({
  customWidth,
  dataTestId = 'forecast-graph-timeline-slider',
  graphId,
  graphWidth,
  leftLastDataPointPosition,
  leftNowPosition,
  leftPosition,
  numberOfDays,
  renderTooltip,
  timeLabel,
  timestamp,
  trackGraphScrubbed,
  utcOffset,
}) => {
  const graphScrubbedTrackedRef = useRef<boolean>(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [measuredWidth, setMeasuredWidth] = useState(0);
  const [fixedNowPosition, setFixedNowPosition] = useState<number>(-1);
  const [currentSliderValue, setCurrentSliderValue] = useState<number>(0);

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) {
      return undefined;
    }
    const update = () => {
      const w = el.getBoundingClientRect().width;
      setMeasuredWidth(Math.floor(w));
    };
    update();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, [graphWidth]);

  const layoutWidth = useMemo(() => {
    if (!graphWidth || graphWidth <= 0) {
      return 0;
    }
    if (!measuredWidth || measuredWidth <= 0) {
      return graphWidth;
    }
    return Math.min(graphWidth, measuredWidth);
  }, [graphWidth, measuredWidth]);

  const graphToLayoutX = useCallback(
    (x: number) => (graphWidth > 0 ? (x / graphWidth) * layoutWidth : x),
    [graphWidth, layoutWidth],
  );

  const leftPositionForLayout = useMemo(() => {
    if (!isNumber(leftPosition)) {
      return leftPosition;
    }
    return graphToLayoutX(leftPosition);
  }, [leftPosition, graphToLayoutX]);

  useEffect(() => {
    graphScrubbedTrackedRef.current = false;
  }, [trackGraphScrubbed]);

  useEffect(() => {
    if (isNumber(leftNowPosition) && leftNowPosition >= 0) {
      const nowSliderValue = (leftNowPosition / graphWidth) * 100;

      if (Math.abs(currentSliderValue - nowSliderValue) < POSITION_TOLERANCE_THRESHOLD) {
        setFixedNowPosition(leftNowPosition);
      }
    }
  }, [leftNowPosition, currentSliderValue, graphWidth]);

  const checkIfShouldTrackScrubbing = useCallback(() => {
    if (graphScrubbedTrackedRef.current === false) {
      graphScrubbedTrackedRef.current = true;
      trackGraphScrubbed(graphId, {});
    }
  }, [graphId, trackGraphScrubbed]);

  const maxDraggablePercentage = useMemo(() => {
    if (isNumber(leftLastDataPointPosition) && leftLastDataPointPosition > 0 && graphWidth) {
      return (leftLastDataPointPosition / graphWidth) * 100;
    }

    if (customWidth && graphWidth) {
      return (customWidth / graphWidth) * 100;
    }

    return 100;
  }, [leftLastDataPointPosition, customWidth, graphWidth]);

  const sliderValue = useMemo(() => {
    if (!isNumber(leftPosition) || !graphWidth) return 0;

    const percentage = (leftPosition / graphWidth) * 100;
    const clampedPercentage = Math.min(percentage, maxDraggablePercentage);

    if (Math.abs(currentSliderValue - clampedPercentage) > SLIDER_VALUE_UPDATE_THRESHOLD) {
      setCurrentSliderValue(clampedPercentage);
    }

    return clampedPercentage;
  }, [leftPosition, graphWidth, maxDraggablePercentage, currentSliderValue]);

  const handleSliderChange = useCallback(
    (_event: Event, newValue: number | number[]) => {
      const value = Array.isArray(newValue) ? newValue[0] : newValue;

      setCurrentSliderValue(value);

      if (value > maxDraggablePercentage) {
        return;
      }

      const xPercentage = Math.min(value / 100, 0.99);
      renderTooltip(clamp(xPercentage, 0, 1), graphId);
      checkIfShouldTrackScrubbing();
    },
    [checkIfShouldTrackScrubbing, graphId, renderTooltip, maxDraggablePercentage],
  );

  const sliderNowLabelStyles = useMemo(() => {
    const rawNow = fixedNowPosition >= 0 ? fixedNowPosition : leftNowPosition;

    if (!isNumber(rawNow) || rawNow < 0 || !layoutWidth) {
      return { display: 'none' };
    }

    const displayNow = graphToLayoutX(rawNow);
    const halfOfLabelWidth = NOW_LABEL_WIDTH / 2;
    const isPositionAtEnd = displayNow > layoutWidth - NOW_LABEL_WIDTH;

    const position = isPositionAtEnd
      ? layoutWidth - NOW_LABEL_WIDTH
      : displayNow - halfOfLabelWidth;

    return {
      display: 'block',
      left: `${position}px`,
      width: `${NOW_LABEL_WIDTH}px`,
    };
  }, [layoutWidth, leftNowPosition, fixedNowPosition, graphToLayoutX]);

  const sliderContainerStyles = useMemo(
    () => ({
      width: `${layoutWidth}px`,
    }),
    [layoutWidth],
  );

  const unavailableDataAreaStyles = useMemo(
    () => ({
      left: `${maxDraggablePercentage}%`,
      width: `${100 - maxDraggablePercentage}%`,
      borderRadius: maxDraggablePercentage > 0 ? '0 2px 2px 0' : '2px',
    }),
    [maxDraggablePercentage],
  );

  const dividerOffsetPx = useMemo(
    () => (graphWidth > 0 ? (7 * layoutWidth) / graphWidth : 7),
    [graphWidth, layoutWidth],
  );

  const partialUnavailableAreaStyles = useMemo(() => {
    if (
      !customWidth ||
      !leftLastDataPointPosition ||
      !isNumber(leftLastDataPointPosition) ||
      leftLastDataPointPosition <= 0
    ) {
      return null;
    }

    const customWidthPercentage = (customWidth / graphWidth) * 100;
    const actualDataPercentage = (leftLastDataPointPosition / graphWidth) * 100;

    if (actualDataPercentage >= customWidthPercentage) {
      return null;
    }

    return {
      left: `${actualDataPercentage}%`,
      width: `${customWidthPercentage - actualDataPercentage}%`,
    };
  }, [customWidth, leftLastDataPointPosition, graphWidth]);

  return (
    <div ref={rootRef} data-testid={dataTestId} style={{ minWidth: 0, width: '100%' }}>
      <SliderTimeLabel
        containerWidth={layoutWidth}
        leftPosition={leftPositionForLayout}
        sliderHandleWidth={10}
        timeLabel={timeLabel}
        timestamp={timestamp}
        utcOffset={utcOffset}
      />
      <Box className={styles.sliderContainer} style={sliderContainerStyles}>
        <Typography className={styles.nowLabel} style={sliderNowLabelStyles} variant="callout1">
          Now
        </Typography>
        <DayDividers
          containerWidth={layoutWidth}
          dividerOffsetPx={dividerOffsetPx}
          numberOfDays={numberOfDays}
        />
        <Slider
          value={sliderValue}
          onChange={handleSliderChange}
          min={0}
          max={100}
          step={0.1}
          className={styles.slider}
          data-testid="forecast-graph-slider-handle"
          slotProps={{ input: { 'aria-label': 'Forecast time scrubber' } }}
        />
        {maxDraggablePercentage < 100 && (
          <Box
            className={styles.unavailableDataArea}
            style={unavailableDataAreaStyles}
            data-testid="forecast-graph-slider-unavailable-area"
          />
        )}
        {partialUnavailableAreaStyles && (
          <Box
            className={styles.partialUnavailableArea}
            style={partialUnavailableAreaStyles}
            data-testid="forecast-graph-slider-partial-unavailable-area"
          />
        )}
      </Box>
    </div>
  );
};

export default React.memo(ForecastGraphTimelineSlider);
